import * as moment from 'moment';

import { AGE_CALCULATION_OPTION } from '@shared/constants';
import { GENDERS } from '@shared/interfaces';

export interface NICAnalyzerResponse {
  success: boolean;
  data:
    | {
        gender: GENDERS;
        year: string;
        month: string;
        date: string;
      }
    | undefined;
}

export function analyzeNIC(NICNo: string): NICAnalyzerResponse {
  // 1. Structural Validation
  if (!NICNo || (NICNo.length !== 10 && NICNo.length !== 12)) {
    return { success: false, data: undefined };
  }

  const isOldNIC = NICNo.length === 10;
  const oldNicRegex = /^\d{9}[vVxX]$/;
  const newNicRegex = /^\d{12}$/;

  if (
    (isOldNIC && !oldNicRegex.test(NICNo)) ||
    (!isOldNIC && !newNicRegex.test(NICNo))
  ) {
    return { success: false, data: undefined };
  }

  // 2. Extract Year and Days value
  let yearInt = 0;
  let daysOfYear = 0;

  if (isOldNIC) {
    yearInt = 1900 + parseInt(NICNo.substring(0, 2), 10);
    daysOfYear = parseInt(NICNo.substring(2, 5), 10);
  } else {
    yearInt = parseInt(NICNo.substring(0, 4), 10);
    daysOfYear = parseInt(NICNo.substring(4, 7), 10);
  }

  // 3. Gender Validation & Days normalization
  let gender: GENDERS;
  if (daysOfYear > 500) {
    gender = GENDERS.FEMALE;
    daysOfYear -= 500;
  } else {
    gender = GENDERS.MALE;
  }

  // Sri Lankan NIC logic uses a fixed 366-day array max limit
  if (daysOfYear < 1 || daysOfYear > 366) {
    return { success: false, data: undefined };
  }

  const isLeapYear = moment([yearInt]).isLeapYear();

  // 4. Critical Fix: Adjust days for Non-Leap Years
  // If it's a non-leap year and the day represents a date after February 29th (day 60),
  // we must decrement it by 1 to align with standard calendar indexes.
  if (!isLeapYear && daysOfYear > 60) {
    daysOfYear -= 1;
  }
  // Edge case: If it's a non-leap year and daysOfYear is exactly 60, it represents Feb 29 on a non-leap year, which is invalid.
  else if (!isLeapYear && daysOfYear === 60) {
    return { success: false, data: undefined };
  }

  // 5. Safe Date Construction
  // Now we can securely map it using moment because the day index matches the actual year type
  const birthday = moment().year(yearInt).dayOfYear(daysOfYear);

  return {
    success: true,
    data: {
      gender,
      date: birthday.format('DD'),
      month: birthday.format('MM'),
      year: yearInt.toString(),
    },
  };
}

export function oldToNewNIC(NICNo: string) {
  if (NICNo.length < 12) {
    const year = 1900 + parseInt(NICNo.substring(0, 2), 10);
    const days = parseInt(NICNo.substring(2, 5), 10);
    const serial = parseInt(NICNo.substring(5, 9), 10);
    return `${year}${days}0${serial}`;
  } else {
    return NICNo;
  }
}

export function getAge(
  dob: Date,
  age_cal_option: AGE_CALCULATION_OPTION,
  effective_date?: Date
): number {
  let age = 0;

  const date1 = effective_date
    ? new Date(effective_date)
    : new Date(Date.now());
  const date2 = new Date(dob);
  age = date1.getFullYear() - date2.getFullYear();

  if (age_cal_option === AGE_CALCULATION_OPTION.NEAREST_BIRTHDAY) {
    const thisYearDate = date2;
    const nextYearDate = date2;
    thisYearDate.setFullYear(date1.getFullYear());
    const gap_1 = Math.abs(date1.getTime() - thisYearDate.getTime());

    nextYearDate.setFullYear(date1.getFullYear() + 1);
    const gap_2 = Math.abs(nextYearDate.getTime() - date1.getTime());

    if (gap_1 > gap_2) {
      age = age + 1;
    }
  } else if (age_cal_option === AGE_CALCULATION_OPTION.NEXT_BIRTHDAY) {
    if (
      date1.getMonth() > date2.getMonth() ||
      (date1.getMonth() == date2.getMonth() &&
        date1.getDate() >= date2.getDate())
    ) {
      age = age + 1;
    } else if (date1.getMonth() == date2.getMonth()) {
      if (date1.getDate() >= date2.getDate()) {
        age = age + 1;
      }
    }
  }

  return age;
}
